if (!customElements.get("zoom-gallery")) {
  class ZoomGallery extends HTMLElement {
    constructor() {
      super();
      this.zoomSwiper = null;
      this.keydownHandler = null;
    }

    connectedCallback() {
      this.initZoomTriggers();
    }

    initZoomTriggers() {
      const triggers = this.querySelectorAll(".zoom-trigger");
      if (!triggers.length) return;

      triggers.forEach((btn) => {
        btn.addEventListener("click", (e) => this.onZoomTriggerClick(e));
      });
    }

    onZoomTriggerClick(event) {
      event.preventDefault();

      const button = event.currentTarget;
      const swiperEl = button.closest(".swiper");
      if (!swiperEl) return;

      const entries = this.buildZoomSourcesFromSwiper(swiperEl);
      if (!entries.length) return;
      const srcs = entries.map((entry) => entry.src);

      // Match the tapped slide by image, not position: the source list skips video
      // slides and duplicates, and a looped carousel adds clone slides, so neither
      // data-zoom-index nor the slide's DOM index lines up with `srcs`.
      const slideImg = button.closest(".swiper-slide")?.querySelector("img");
      let startIndex = slideImg
        ? entries.findIndex((entry) => entry.key === this.zoomKey(slideImg))
        : -1;

      if (startIndex < 0 && button.dataset.zoomIndex != null) {
        const parsed = parseInt(button.dataset.zoomIndex, 10);
        if (!Number.isNaN(parsed)) startIndex = parsed;
      }
      startIndex = Math.min(Math.max(startIndex, 0), srcs.length - 1);

      this.openZoomOverlay(srcs, startIndex);
    }

    // Stable identity for an image regardless of which srcset candidate has loaded.
    zoomKey(img) {
      return (img.getAttribute("src") || "").split("?")[0];
    }

    // currentSrc is sized for the carousel (often ~400w on mobile), too soft to
    // pinch into. Take the widest srcset candidate instead.
    largestSrcsetCandidate(img) {
      const srcset = img.getAttribute("srcset");
      if (!srcset) return "";
      let best = "";
      let bestWidth = 0;
      srcset.split(",").forEach((candidate) => {
        const [url, descriptor] = candidate.trim().split(/\s+/);
        const width = parseInt(descriptor, 10) || 0;
        if (url && width >= bestWidth) {
          best = url;
          bestWidth = width;
        }
      });
      return best;
    }

    buildZoomSourcesFromSwiper(swiperEl) {
      const slides = Array.from(swiperEl.querySelectorAll(".swiper-slide"));
      const entries = [];

      slides.forEach((slide) => {
        if (slide.querySelector(".video-modal__play")) {
          return;
        }

        const img = slide.querySelector("img");
        if (!img) return;

        const src =
          img.getAttribute("data-zoom-src") ||
          this.largestSrcsetCandidate(img) ||
          img.currentSrc ||
          img.getAttribute("src") ||
          "";

        if (!src) return;
        if (src.includes(".svg")) return;

        const key = this.zoomKey(img) || src;
        if (!entries.some((entry) => entry.key === key)) {
          entries.push({ key, src });
        }
      });

      return entries;
    }

    renderArrow() {
      return `
      <svg width="12" viewBox="0 0 12 22" xmlns="http://www.w3.org/2000/svg">
        <g fill="none" stroke-linejoin="round">
          <path d="M1.7 1.6L10.6 10.6C10.7 10.7 10.8 10.8 10.8 11 10.8 11.2 10.7 11.3 10.6 11.4L1.7 20.4" stroke-width="1.6" stroke="black"></path>
        </g>
      </svg>
    `;
    }

    renderClose() {
      return `
      <svg width="60" height="60" xmlns="http://www.w3.org/2000/svg">
        <g fill="none" fill-rule="evenodd">
          <circle fill="rgb(243, 243, 243)" opacity="1" cx="30" cy="30" r="30"></circle>
          <g stroke="#000" stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5">
            <path d="m21 21 18 18M39 21 21 39"></path>
          </g>
        </g>
      </svg>
    `;
    }

    // .swiper-zoom-container is what Swiper's Zoom module scales (pinch, double-tap,
    // drag to pan). It owns the img transform, so the img itself must not use one.
    renderZoomItem(src) {
      return `
      <div class="swiper-slide">
        <div class="swiper-zoom-container w-full h-screen lg:p-6">
          <img class="zoom-img w-full h-full object-contain" src="${src}" />
        </div>
      </div>
    `;
    }

    renderZoomOverlay(srcs) {
      return `
      <div class="zoom-content fixed w-full h-full bg-gray-75 inset-0 z-50">
        <button class="zoom-close absolute top-5 right-5 transform-gpu transition-all scale-100 hover:scale-90 z-10">
          ${this.renderClose()}
        </button>
        <div class="h-screen w-full relative">
          <button class="zoom-prev p-4 transform rotate-180 absolute left-6 top-1/2 -translate-y-1/2 z-10">
            ${this.renderArrow()}
          </button>
          <div class="zoom-carousel swiper w-full h-full mix-blend-multiply">
            <div class="swiper-wrapper">
              ${srcs.map((src) => this.renderZoomItem(src)).join("")}
            </div>
          </div>
          <button class="zoom-next p-4 transform -translate-y-1/2 absolute right-6 top-1/2 z-10">
            ${this.renderArrow()}
          </button>
        </div>
      </div>
    `;
    }

    openZoomOverlay(srcs, startIndex) {
      if (!srcs || !srcs.length) return;

      if (this.zoomSwiper) {
        this.closeZoomOverlay();
      }

      const body = document.body;
      const wrapper = document.createElement("div");
      wrapper.innerHTML = this.renderZoomOverlay(srcs);

      const zoomContent = wrapper.firstElementChild;
      body.appendChild(zoomContent);
      body.classList.add("overflow-hidden");

      const zoomCarouselEl = zoomContent.querySelector(".zoom-carousel.swiper");

      const hasMultiple = srcs.length > 1;

      this.zoomSwiper = new Swiper(zoomCarouselEl, {
        slidesPerView: 1,
        spaceBetween: 0,
        loop: hasMultiple,
        zoom: { maxRatio: 3 },
      });

      if (hasMultiple && typeof this.zoomSwiper.slideToLoop === "function") {
        this.zoomSwiper.slideToLoop(startIndex, 0);
      } else {
        this.zoomSwiper.slideTo(startIndex, 0);
      }

      const closeBtn = zoomContent.querySelector(".zoom-close");
      const prevBtn = zoomContent.querySelector(".zoom-prev");
      const nextBtn = zoomContent.querySelector(".zoom-next");

      if (!hasMultiple) {
        if (prevBtn) prevBtn.style.display = "none";
        if (nextBtn) nextBtn.style.display = "none";
      }

      if (prevBtn) {
        prevBtn.addEventListener("click", () => {
          if (this.zoomSwiper) this.zoomSwiper.slidePrev();
        });
      }

      if (nextBtn) {
        nextBtn.addEventListener("click", () => {
          if (this.zoomSwiper) this.zoomSwiper.slideNext();
        });
      }

      if (closeBtn) {
        closeBtn.addEventListener("click", () => {
          this.closeZoomOverlay();
        });
      }

      this.setupKeyAndFocusHandling(closeBtn, prevBtn, nextBtn);
    }

    setupKeyAndFocusHandling(closeBtn, prevBtn, nextBtn) {
      const focusable = [closeBtn, prevBtn, nextBtn].filter(Boolean);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      this.keydownHandler = (e) => {
        const isTab = e.key === "Tab" || e.keyCode === 9;
        const isEsc = e.key === "Escape" || e.keyCode === 27;
        const isLeft = e.key === "ArrowLeft";
        const isRight = e.key === "ArrowRight";

        if (isEsc) {
          this.closeZoomOverlay();
          return;
        }

        if (isLeft && this.zoomSwiper) {
          this.zoomSwiper.slidePrev();
          return;
        }

        if (isRight && this.zoomSwiper) {
          this.zoomSwiper.slideNext();
          return;
        }

        if (!isTab || !focusable.length) return;

        if (e.shiftKey) {
          if (document.activeElement === first) {
            last.focus();
            e.preventDefault();
          }
        } else {
          if (document.activeElement === last) {
            first.focus();
            e.preventDefault();
          }
        }
      };

      document.addEventListener("keydown", this.keydownHandler);

      if (first) first.focus();
    }

    closeZoomOverlay() {
      const body = document.body;

      if (this.zoomSwiper) {
        this.zoomSwiper.destroy(true, true);
        this.zoomSwiper = null;
      }

      const zoomContent = document.querySelector(".zoom-content");
      if (zoomContent && zoomContent.parentNode) {
        zoomContent.parentNode.removeChild(zoomContent);
      }

      body.classList.remove("overflow-hidden");

      if (this.keydownHandler) {
        document.removeEventListener("keydown", this.keydownHandler);
        this.keydownHandler = null;
      }
    }
  }

  customElements.define("zoom-gallery", ZoomGallery);
}
